// src/validation-schemas/profile-update-schema.ts

import { z } from "zod";

const US_STATE_ABBREVIATIONS = [
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL",
    "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME",
    "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH",
    "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI",
    "SC", "SD", "TN", "TX", "UT", "VT", "VI", "VA", "WA", "WV",
    "WI", "WY"
] as const;

export const formatPhoneNumber = (input: string): string => {
    const cleaned = input.replace(/\D/g, "");
    const length = cleaned.length;

    if (length === 0) return "";
    if (length < 4) return `(${cleaned}`;
    if (length < 7) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;

    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
};

const cityRegex = /^[A-Za-zÀ-ÿ .'-]+$/;
const strictNameRegex = /^[A-Za-zÀ-ÿ'.-]+$/;

export const createNameSchema = (fieldLabel: string) =>
    z
        .string()
        .nonempty(`${fieldLabel} is required.`)
        .min(2, `${fieldLabel} must be at least 2 characters.`)
        .max(50, `${fieldLabel} must be less than 50 characters.`)
        .refine(val => val.trim().length > 0, {
            message: `${fieldLabel} cannot be only whitespace.`,
        })
        .refine(val => !/\s/.test(val), {
            message: `${fieldLabel} cannot contain spaces.`,
        })
        .refine(val => strictNameRegex.test(val), {
            message: `${fieldLabel} contains invalid characters.`,
        });

export const updateProfileSchema = z.object({
    firstName: createNameSchema("First name"),
    lastName: createNameSchema("Last name"),

    dateOfBirth: z.coerce.date({
        required_error: "Date of Birth is required",
        invalid_type_error: "Invalid date format",
    })
        .refine((date) => date <= new Date(), {
            message: "Date of Birth cannot be in the future",
        })
        .refine((date) => {
            const today = new Date();
            let age = today.getFullYear() - date.getFullYear();
            const m = today.getMonth() - date.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < date.getDate())) {
                age--;
            }
            return age >= 18;
        }, {
            message: "You must be at least 18 years old",
        }),
    phoneNumber: z
        .string()
        .nonempty("Phone number is required")
        .transform(val => {
            let cleaned = val.replace(/\D/g, '');
            if (cleaned.length === 11 && cleaned.startsWith('1')) {
                cleaned = cleaned.slice(1);
            }
            return cleaned;
        })
        .refine(val => val.length === 10, {
            message: "Phone number must be exactly 10 digits (excluding country code)"
        })
        .refine(val => /^[2-9]\d{2}[2-9]\d{2}\d{4}$/.test(val), {
            message: "Invalid US phone number format. Area code and exchange code cannot start with 0 or 1"
        })
        .refine(val => !['555'].includes(val.slice(3, 6)), {
            message: "555 exchange codes are not valid for real phone numbers"
        })
        .transform(val => {
            return formatPhoneNumber(val);
        }),

    email: z
        .string()
        .nonempty("Email is required")
        .email("Invalid email address")
        .transform(val => val.toLowerCase().trim())
        .refine(val => val.length <= 320, "Email address is too long")
        .refine(val => {
            const domain = val.split('@')[1];
            return domain && domain.includes('.') && domain.length >= 4;
        }, "Invalid email domain"),

    address: z
        .string()
        .nonempty("Address is required")
        .max(75, "Address must be less than or equal to 75 characters")
        .refine((val) => val.trim().length > 0, {
            message: "Address cannot be only whitespace",
        }),

    address2: z
        .string()
        .trim()
        .max(75, "Address 2 must be less than or equal to 75 characters")
        .refine((val) => val.trim().length > 0, {
            message: "Address 2 cannot be only whitespace",
        })
        .optional()
        .or(z.literal("").transform(() => undefined)),

    city: z
        .string()
        .nonempty("City is required")
        .trim()
        .min(2, "City must be at least 2 characters")
        .max(50, "City must be less than 50 characters")
        .refine(val => cityRegex.test(val), {
            message: "City name contains invalid characters"
        }),

    state: z
        .string()
        .nonempty("State is required")
        .trim()
        .refine(val => US_STATE_ABBREVIATIONS.includes(val as any), {
            message: "Please select a valid US state"
        }),

    zipCode: z
        .string()
        .nonempty("ZIP code is required")
        .trim()
        .refine(val => /^\d{5}(-\d{4})?$/.test(val), {
            message: "Invalid ZIP code format. Use XXXXX or XXXXX-XXXX"
        })
});

export type UpdateProfileSchema = z.infer<typeof updateProfileSchema>;
